import { useClickOutside } from "@mantine/hooks";
import classNames from "classnames";
import { useState } from "react";
import { AngleDownIcon } from "@/public/icons/AngleDownIcon";
import { AngleUpIcon } from "@/public/icons/AngleUpIcon";
import styles from "./ProfileSelect.module.scss";

type SelectInputProps<T> = {
  label: string;
  initialIndex: number;
  setSelectedValue: (value: T) => void;
  selectOptions: T[];
  selectOptionsLabels: string[];
  isDisabled?: boolean;
  isError?: boolean;
};

export const setInitialValue = (
  initialValue: unknown,
  optionList: unknown[]
): number => {
  for (let i = 0; i < optionList.length; i++) {
    if (initialValue === optionList[i]) {
      return i;
    }
  }

  return 0;
};

export const SelectInput = <T,>({
  label,
  initialIndex,
  setSelectedValue,
  selectOptions,
  selectOptionsLabels,
  isDisabled,
  isError,
}: SelectInputProps<T>) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(initialIndex);
  const [optionListOpened, setOptionListOpened] = useState<boolean>(false);
  const ref = useClickOutside(() => setOptionListOpened(false));

  const setIsOpenedSelect = (value: boolean) => {
    if (!isDisabled) {
      setTimeout(() => {
        setOptionListOpened(value);
      }, 100);
    }
  };

  const setSelectedOption = (value: T, index: number) => {
    setSelectedIndex(index);
    setSelectedValue(value);
    setIsOpenedSelect(false);
  };

  return (
    <div className={styles.selectInputGroup}>
      <div className={styles.inputLabel}>{label}</div>
      <div className={styles.inputGroup}>
        <input
          className={classNames(styles.textInput, {
            [styles.inputDisabled]: isDisabled,
          })}
          disabled={isDisabled}
          value={selectOptionsLabels[selectedIndex]}
          readOnly
          onClick={() => setIsOpenedSelect(true)}
        />
        <div
          className={classNames(styles.optionList, {
            [styles.visible]: optionListOpened,
          })}
          ref={ref}
        >
          {selectOptions.map((option, index) => (
            <div
              className={styles.option}
              onClick={() => setSelectedOption(option, index)}
              key={index}
            >
              {selectOptionsLabels[index]}
            </div>
          ))}
        </div>
        <div className={styles.arrow}>
          {optionListOpened && (
            <AngleUpIcon
              colorClass={classNames({
                [styles.arrowColor]: !isError && !isDisabled,
                [styles.arrowDisabled]: !isError && isDisabled,
                [styles.arrowError]: isError && !isDisabled,
              })}
            />
          )}
          {!optionListOpened && (
            <AngleDownIcon
              colorClass={classNames({
                [styles.arrowColor]: !isError && !isDisabled,
                [styles.arrowDisabled]: !isError && isDisabled,
                [styles.arrowError]: isError && !isDisabled,
              })}
            />
          )}
        </div>
      </div>
    </div>
  );
};
